/**
 * FCM (Firebase Cloud Messaging) via @capacitor/push-notifications.
 * Wordt alléén gebruikt in de Android-Capacitor-build — de PWA-tak
 * blijft op Web Push (VAPID) via lib/pwa.ts.
 *
 * Flow: register() → 'registration'-event geeft een FCM-token →
 * we sturen 'm naar de server. Bij notification-tap krijgen we de
 * payload met conversation_id en open de juiste conversatie.
 */

import { api } from "./api";
import { capacitorDeviceLabel, isCapacitor } from "./capacitor";

let currentToken: string | null = null;
let listenersRegistered = false;

type ListenerHandle = { remove: () => Promise<void> };

type RegistrationOutcome = { ok: true } | { ok: false; reason: string };

// De uitkomst van een register()-poging komt asynchroon binnen via de
// 'registration'/'registrationError'-events. Deze resolver koppelt die
// events terug aan de wachtende enableFcmNotifications-aanroep zodat de
// UI een écht resultaat kan tonen i.p.v. altijd "ok" — voorheen leek
// alles te werken terwijl het token nooit bij de server aankwam.
let pendingRegistration: ((outcome: RegistrationOutcome) => void) | null = null;

function resolvePendingRegistration(outcome: RegistrationOutcome): void {
  const resolve = pendingRegistration;
  pendingRegistration = null;
  if (resolve) resolve(outcome);
}

function waitForRegistrationOutcome(timeoutMs: number): Promise<RegistrationOutcome> {
  return new Promise<RegistrationOutcome>((resolve) => {
    pendingRegistration = resolve;
    setTimeout(() => {
      if (pendingRegistration === resolve) {
        pendingRegistration = null;
        resolve({ ok: false, reason: "timeout" });
      }
    }, timeoutMs);
  });
}

/** Vraagt de notification-permissie, registreert bij FCM en sluit
 *  af door het token naar de server te sturen. Caller moet zorgen
 *  dat hij vanuit een user-gesture aanroept (Android 13+ permissie).
 *  Wacht op het registration-event (of de error) zodat het resultaat
 *  klopt: `ok` betekent dat het token daadwerkelijk bij de server staat. */
export async function enableFcmNotifications(): Promise<
  { ok: true } | { ok: false; reason: string }
> {
  if (!isCapacitor()) return { ok: false, reason: "not_native" };

  let PushNotifications: typeof import("@capacitor/push-notifications").PushNotifications;
  try {
    ({ PushNotifications } = await import("@capacitor/push-notifications"));
  } catch {
    return { ok: false, reason: "plugin_missing" };
  }

  // installListeners zet alle event-handlers eenmalig. Idempotent.
  await installListeners(PushNotifications);

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return { ok: false, reason: "denied" };

  const outcome = waitForRegistrationOutcome(15_000);
  try {
    await PushNotifications.register();
  } catch {
    resolvePendingRegistration({ ok: false, reason: "register_failed" });
  }

  return outcome;
}

/** Her-registratie bij app-start en bij resume, zonder user-gesture. FCM-tokens roteren,
 *  en server-side worden tokens gewist bij sessie-revoke (FK CASCADE) of
 *  wanneer FCM ze als UNREGISTERED bestempelt. Zonder deze her-registratie
 *  vielen meldingen dan permanent stil: de "Meldingen aanzetten"-knop is
 *  na de eerste keer verdwenen (permissie is immers granted) en niets
 *  stuurde het token ooit opnieuw naar de server. Aanroepen bij elke
 *  app-start én resume; no-op zolang de permissie niet granted is. */
export async function reRegisterFcmIfGranted(): Promise<void> {
  if (!isCapacitor()) return;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") return;
    await installListeners(PushNotifications);
    await PushNotifications.register();
  } catch (err) {
    console.error("[fcm] re-register failed:", err);
  }
}

/** "granted" | "denied" | "prompt" — uit Capacitor's PushNotifications-
 *  plugin. "prompt" = nog niet gevraagd (toon knop). In niet-Capacitor-
 *  context: "prompt" als sentinel zodat de caller de Web-Push-tak pakt. */
export async function getFcmPermissionStatus(): Promise<
  "granted" | "denied" | "prompt"
> {
  if (!isCapacitor()) return "prompt";
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const p = await PushNotifications.checkPermissions();
    if (p.receive === "granted") return "granted";
    if (p.receive === "denied") return "denied";
    return "prompt";
  } catch {
    return "prompt";
  }
}

export async function disableFcmNotifications(): Promise<void> {
  if (!isCapacitor()) return;
  if (!currentToken) return;
  try {
    await api.unsubscribeFcm(currentToken);
  } catch {
    /* */
  }
  currentToken = null;
}

/** Open-conversation-callback die we vanuit Main.tsx registreren. Bij
 *  een tap op een FCM-melding (app in achtergrond/gekild) opent de OS
 *  de app en levert de payload via pushNotificationActionPerformed. */
let openConversationCallback: ((id: number) => void) | null = null;

export function setOpenConversationCallback(cb: ((id: number) => void) | null): void {
  openConversationCallback = cb;
}

async function installListeners(
  PushNotifications: typeof import("@capacitor/push-notifications").PushNotifications,
): Promise<void> {
  if (listenersRegistered) return;
  listenersRegistered = true;

  const handles: ListenerHandle[] = [];

  // FCM-token gekregen — sturen naar server zodat 'ie in
  // swift_fcm_tokens belandt en pushToUsers 'm vindt. Pas als die
  // upload slaagt is de registratie echt "ok" — een 503 betekent dat
  // FCM_SERVICE_ACCOUNT_JSON op de server ontbreekt.
  handles.push(
    (await PushNotifications.addListener("registration", async (token) => {
      currentToken = token.value;
      try {
        await api.subscribeFcm({
          fcm_token: token.value,
          device_label: capacitorDeviceLabel(),
        });
        resolvePendingRegistration({ ok: true });
      } catch (err) {
        console.error("[fcm] server subscribe failed:", err);
        resolvePendingRegistration({ ok: false, reason: "server_failed" });
      }
    })) as unknown as ListenerHandle,
  );

  handles.push(
    (await PushNotifications.addListener("registrationError", (err) => {
      console.error("[fcm] registration error:", err);
      resolvePendingRegistration({ ok: false, reason: "registration_error" });
    })) as unknown as ListenerHandle,
  );

  // Voorgrond: notification arriveert terwijl de app open is. We tonen
  // 'm niet als systeem-notification (de gebruiker ziet 'm sowieso in
  // de stream binnen-een-seconde via socket.io); doen ook niets met
  // de payload. Mocht het bericht uit een nét-niet-actieve conversatie
  // komen dan zorgt de bestaande Main.tsx-handler voor toast+geluid.
  handles.push(
    (await PushNotifications.addListener(
      "pushNotificationReceived",
      () => undefined,
    )) as unknown as ListenerHandle,
  );

  // Tap op een melding (app achtergrond/gekild). Conversation_id zit
  // in data; springen er heen.
  handles.push(
    (await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action) => {
        const cid = action.notification.data?.conversation_id;
        const n = typeof cid === "string" ? Number.parseInt(cid, 10) : (cid as number | undefined);
        if (typeof n === "number" && Number.isFinite(n) && openConversationCallback) {
          openConversationCallback(n);
        }
      },
    )) as unknown as ListenerHandle,
  );
  void handles;
}
